import { getDisplayDate, NumberFormat, TableColumn, TableData } from '@billy/design-system'
import { TFunction } from 'i18next'

import { sanitizeCurrencyForPdf } from '@features/exports/utils/sanitizeCurrencyForPdf'

import { Currency } from '../../../../../../../../../types/currency'
import { LocaleIdentifier } from '../../../../../../../../../types/localeIdentifier'
import { DEFAULT_CURRENCY_NUMBER_FORMAT_OPTIONS } from '../../../../../../../constants/defaultCurrencyNumberFormatOptions'
import { SalesByItemReportTableAccessor } from '../../../../../../../enums/salesByItemReportTableAccessor'
import { getProductTypeLabel } from '../../../../../../../utils/getProductTypeLabel'
import { salesReportCurrencyColumnsAccessors } from '../constants/salesReportCurrencyColumnsAccessors'
import { salesReportDateColumnsAccessors } from '../constants/salesReportDateColumnsAccessors'

const isDateColumn = <T extends TableData>(column: TableColumn<T>): boolean =>
  typeof column.accessor === 'string' && salesReportDateColumnsAccessors.includes(column.accessor)

const isCurrencyColumn = <T extends TableData>(column: TableColumn<T>): boolean =>
  typeof column.accessor === 'string' && salesReportCurrencyColumnsAccessors.includes(column.accessor)

const isProductTypeColumn = <T extends TableData>(column: TableColumn<T>): boolean =>
  column.accessor === SalesByItemReportTableAccessor.ItemType

interface GetCellDisplayValueProps<T extends TableData> {
  column: TableColumn<T>
  currency: Currency
  locale: LocaleIdentifier
  row: T
  t: TFunction
}

export const getCellDisplayValue = <T extends TableData>({
  column,
  currency,
  locale,
  row,
  t,
}: GetCellDisplayValueProps<T>): string => {
  if (typeof column.accessor !== 'string') {
    return ''
  }

  const cellValue = row[column.accessor] ?? ''

  if (isDateColumn(column)) {
    return cellValue ? getDisplayDate(new Date(cellValue?.toString()), locale) : '-'
  } else if (isCurrencyColumn(column) && typeof cellValue === 'number') {
    const currencyValue = NumberFormat.getCurrencyNumber(
      cellValue,
      currency,
      locale,
      DEFAULT_CURRENCY_NUMBER_FORMAT_OPTIONS,
    )

    return sanitizeCurrencyForPdf(currencyValue)
  } else if (isProductTypeColumn(column)) {
    return getProductTypeLabel(cellValue?.toString(), t)
  }

  return cellValue?.toString()
}
